import { window, document } from "global";
import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import _debounce from "lodash/debounce";
import { useGlobal } from "reactn";
import { useHistory, useLocation } from "react-router-dom";
import { useLazyQuery, useQuery } from "@apollo/client";
import MediaQuery from "components/MediaQuery";
import Autosuggest from "react-autosuggest";
import AnimateOnChange from "react-animate-on-change";
import RegionSelector from "components/Masthead/RegionSelector";
import ReactSVG from "react-svg";
import { useBestSearchMatch } from "lib/searchHelper.js";
import { getRiotAssetsContext } from "@outplayed/riot-assets";
import _uniqby from "lodash.uniqby";
import { getBlockImg, getShortName } from "lib/region_helper.js";
import { useSearchBarPages } from "lib/hooks/searchHooks";
import { LANDING_PAGE_OPEN_SEARCHES_ONLY_ONCE_PER_SESSION } from "components/OpenSearchRedirect";
import { generateBuildAndRunes } from "lib/FilterPreferences/QueryParams";
import { useManualSummonerProfile, useMultisearch } from "api/summonerProfileApi";
import { useTraceUpdate } from "lib/hooks/testingHooks";
import { useLightMode } from "hooks/general-hooks";
import { GET_USER_STATE } from "api/graphql/queries";
import { stripMultiSearch, stripMultiSearchBar } from "./MultiSearch/multi-search-filter";
import ItemTemp from "@outplayed/tooltips/src/tooltipImages/ItemTemp";
import { PLAYER_INFO_SUGGESTIONS } from "api/graphql/queries";
import { useCompare } from "hooks/general-hooks";
import RegionSelectorV2 from "./Masthead/RegionSelectorV2";
import { ReactComponent as SearchIcon } from "svg/search-icon-2.svg";
import { ReactComponent as SearchIconWhite } from "svg/search-icon-2-white.svg";
import { ReactComponent as SearchIconWhiteLightMode } from "svg/search-icon-2-light-mode.svg";

// https://developer.mozilla.org/en/docs/Web/JavaScript/Guide/Regular_Expressions#Using_Special_Characters
function escapeRegexCharacters(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function useSuggestions() {
  const { data: searchBarPages, loading: loadingSearchBarPages, error: errorSearchBarPages } = useSearchBarPages();

  return (value, clientRegion, playerInfoSuggestions) => {
    if (!searchBarPages || loadingSearchBarPages || errorSearchBarPages) {
      return [];
    }

    const escapedValue = escapeRegexCharacters(value.trim());

    if (escapedValue === "") return [];
    const regex = new RegExp("^" + escapedValue, "i");

    let suggestions = searchBarPages
      .map((section) => {
        return {
          title: section.title,
          pages: _uniqby(
            section.pages.filter((page) => regex.test(page.key)),
            (page) => page.displayName,
          ),
        };
      })
      .filter((section) => section.pages.length > 0);

    let finalSuggestions = [
      {
        title: "Summoner Profile",
        pages:
          playerInfoSuggestions.length === 0
            ? [
                {
                  key: "summonerProfile",
                  displayName: value,
                  url: `/lol/profile/${clientRegion}/${value}/overview`,
                  regionId: clientRegion,
                  section: "summoner",
                },
              ]
            : playerInfoSuggestions.map(({ puuidV4, summonerName, iconId, summonerLevel }) => ({
                key: puuidV4,
                displayName: summonerName,
                url: `/lol/profile/${clientRegion}/${summonerName}/overview`,
                regionId: clientRegion,
                section: "summoner",
                iconId,
                summonerLevel,
              })),
      },
    ];

    if (suggestions.length > 0) {
      finalSuggestions.unshift(suggestions.shift());
      finalSuggestions = finalSuggestions.concat(suggestions);
    }

    return finalSuggestions;
  };
}

function getSuggestionValue(suggestion) {
  return suggestion.displayName;
}

function useRenderSuggestion(suggestion) {
  const { getChampionImgFromSprite } = getRiotAssetsContext();
  const { getProfileIconImg } = getRiotAssetsContext();

  return (suggestion) => {
    let img;
    if (suggestion.section === "summoner") {
      img = <img src={getProfileIconImg(suggestion.iconId || 1)} className="summoner-icon" />;
    } else if (suggestion.section === "items") {
      img = <ItemTemp itemId={suggestion.itemId} isSprite />;
    } else {
      img = getChampionImgFromSprite(suggestion.champId, { size: 24 });
    }

    let typeImg;
    if (suggestion.section === "pro-build") {
      typeImg = (
        <img className="page-type" src={"https://static.bigbrain.gg/assets/ugg/icons/search_probuilds.svg"} alt="Pro Build" />
      );
    } else if (suggestion.section === "counters") {
      typeImg = (
        <img className="page-type" src={"https://static.bigbrain.gg/assets/ugg/icons/search_counters.svg"} alt="Counters" />
      );
    }

    return (
      <div className="render-suggestion">
        <div>
          {typeImg}
          {img}
        </div>
        <div className="suggest-text">
          {suggestion.displayName}
          <span className="suggest-text-secondary">
            {suggestion.section == "summoner" && suggestion.summonerLevel && `Lvl ${suggestion.summonerLevel}`}
          </span>
        </div>
        {suggestion.section == "summoner" && <img className="suggest-right" src={getBlockImg(suggestion.regionId, true)} />}
      </div>
    );
  };
}

function renderSectionTitle(section) {
  return <strong>{section.title}</strong>;
}

function getSectionSuggestions(section) {
  return section.pages;
}

function getRecentlySearched() {
  if (window) {
    const recentlySearched = localStorage.getItem("recentlySearched");
    if (recentlySearched !== null) {
      return [
        {
          title: "Recently Searched",
          pages: JSON.parse(recentlySearched),
        },
      ];
    }
  }

  return [];
}

const SearchBar = (props) => {
  const history = useHistory();
  const location = useLocation();
  const inputRef = useRef();
  const { onSearchAmplitudeLogging, goToLiveGame } = props;
  const initInteracting = useRef(false);
  const [init, setInit] = useState(false);
  const [isInteracting, setInteraction] = useState(false);
  const [value, setValue] = useState("");
  const [suggestions, setSuggestions] = useState(getRecentlySearched());
  const [highlightedSuggestion, setHighlightedSuggestion] = useState(null);
  const [lastPage, setLastPage] = useState(undefined);
  const [lastSearchValue, setLastSearchValue] = useState(undefined);
  const [searchResults, setSearchResults] = useState(undefined);
  const [searching, setSearching] = useState(false);
  const [multiSearching, setMultisearching] = useState(false);
  const [isCommaList, setIsCommaList] = useState(false);
  const [isParsedText, setIsParsedText] = useState(false);
  const [clientRegion] = useGlobal("clientRegion");
  const [playerInfoSuggestions, setPlayerInfoSuggestions] = useState([]);
  const { lightModeOn } = useLightMode();
  const playerInfoSuggestionsChanged = useCompare(JSON.stringify(playerInfoSuggestions));

  const { data: userState } = useQuery(GET_USER_STATE);
  const { filterDefaults } = (userState && userState.getState) || {};
  const championSearchParams = useMemo(() => generateBuildAndRunes(filterDefaults), [filterDefaults]);

  const [getSummonerProfile, { loading, data, error }] = useManualSummonerProfile(clientRegion, value, null, null, null);

  const [getPlayerInfoSuggestions, { loading: playerInfoSuggestionsLoading, data: playerInfoSuggestionsData = {} }] =
    useLazyQuery(PLAYER_INFO_SUGGESTIONS, {
      ssr: false,
      fetchPolicy: "no-cache",
    });

  const debouncedGetPlayerInfoSuggestions = useCallback(_debounce(getPlayerInfoSuggestions, 200), []);

  const {
    autoFocus,
    isLanding,
    animate,
    placeholder,
    materialDesign,
    isMasthead,
    forceMastheadRegion,
    noDropdown,
    defaultValue,
    multisearchOnly,
    isLandingMobile,
  } = props;

  const regionMinSize = props.regionMinSize ? props.regionMinSize : "TABLET";

  const getBestSearchMatch = useBestSearchMatch();
  const getSuggestions = useSuggestions();
  const renderSuggestion = useRenderSuggestion();

  const changeUrl = (page, lastSearchValue) => {
    if (page.targetHref) {
      window && window.open(page.url);
    } else if (page.url) {
      setLastSearchValue(lastSearchValue);
      setLastPage(page.url);
      if (location.pathname !== page.url) {
        window && window.scrollTo(0, 0);
        history.push({
          pathname: page.url,
          search: championSearchParams || "",
        });
      }
    }
  };

  const search = (val) => {
    const commaList = val
      .split(",")
      .map((entry) => entry.trim())
      .filter((entry) => entry !== "");
    const { isMultiSearch, names } = stripMultiSearchBar(val);
    if (commaList.length > 1 || multisearchOnly) {
      setIsCommaList(true);
      setMultisearching(true);
    } else if (isMultiSearch && names?.length > 0) {
      setIsParsedText(true);
      setMultisearching(true);
    } else {
      setIsCommaList(false);
      if (val.trim() === "") return;
      const searchValue = commaList[0]; // has one valid search value
      const matches = getBestSearchMatch(searchValue);
      const noNicknames = matches.filter((match) => !match.item.isNickname);
      // Check exact name
      const hasExactMatch = matches.length > 0 && Math.round(matches[0].score * 1000000) / 1000000 === 0;
      // Check exact nickname
      const noNicknameMatch = noNicknames.length > 0 && noNicknames[0];
      let resToUse = hasExactMatch && matches[0];
      // Check name substring match
      if (noNicknameMatch) {
        const strippedMatchValue = noNicknameMatch.item.key.toLowerCase();
        const strippedSearchValue = searchValue.toLowerCase();
        if (strippedMatchValue.indexOf(strippedSearchValue) === 0 && noNicknameMatch.item.isDefaultPage === true) {
          resToUse = noNicknameMatch;
        }
      }

      onSearchAmplitudeLogging && onSearchAmplitudeLogging();
      if (resToUse) {
        changeUrl(resToUse.item, searchValue);
        // Add to recently recently searched
        if (window) {
          const recentlySearched = localStorage.getItem("recentlySearched");
          const newEntry = resToUse.item;
          newEntry.champId = newEntry.id;
          if (recentlySearched === null) {
            localStorage.setItem("recentlySearched", JSON.stringify([newEntry]));
          } else {
            const parsed = JSON.parse(recentlySearched);
            const filtered = parsed.filter((item) => item.displayName !== resToUse.item.displayName);
            filtered.unshift(newEntry);
            localStorage.setItem("recentlySearched", JSON.stringify(filtered.slice(0, 6)));
          }
        }
      } else {
        getSummonerProfile();
        setSearchResults(matches);
        setLastSearchValue(searchValue);
        setSearching(true);
      }
    }
  };

  const handleKeyPress = useCallback(
    (e) => {
      let searchBar = inputRef && inputRef.current;

      if (e.which === 13) {
        // on enter
        const val = searchBar && searchBar.value;
        if (!val && !isCommaList) return;

        if (highlightedSuggestion && highlightedSuggestion.url) {
          if (highlightedSuggestion.targetHref) {
            return window && window.open(highlightedSuggestion.url);
          } else {
            return history.push({
              pathname: highlightedSuggestion.url,
              search: championSearchParams.searchParams || "",
            });
          }
        }

        search(val);
        searchBar.blur();
      }
    },
    [search, highlightedSuggestion],
  );

  useEffect(() => {
    setInit(true);

    if (defaultValue) {
      setValue(defaultValue);
    }
  }, []);

  useEffect(() => {
    if (
      init
      // init &&
      // (LANDING_PAGE_OPEN_SEARCHES_ONLY_ONCE_PER_SESSION ||
      // !isLanding)
    ) {
      if (inputRef && inputRef.current) {
        inputRef.current.addEventListener("keypress", handleKeyPress);

        return () => {
          if (inputRef && inputRef.current) {
            inputRef.current.removeEventListener("keypress", handleKeyPress);
          }
        };
      }
    }
  }, [init, handleKeyPress]);

  useEffect(() => {
    if (isInteracting) {
      let suggestions = getSuggestions(value, clientRegion, playerInfoSuggestions);
      if (value.trim().length === 0) {
        suggestions = getRecentlySearched();
      }
      setSuggestions(suggestions);
    }
  }, [isInteracting]);

  useEffect(() => {
    if (!playerInfoSuggestionsLoading) {
      setPlayerInfoSuggestions(playerInfoSuggestionsData.playerInfoSuggestions || []);
    }
  }, [playerInfoSuggestionsLoading, JSON.stringify(playerInfoSuggestionsData)]);

  useEffect(() => {
    if (playerInfoSuggestionsChanged) {
      setSuggestions(getSuggestions(value, clientRegion, playerInfoSuggestions));
    }
  }, [value, clientRegion, JSON.stringify(playerInfoSuggestions), playerInfoSuggestionsChanged]);

  useEffect(() => {
    if (lastPage && location.pathname !== lastPage && value === lastSearchValue) {
      setValue("");
      setLastPage(undefined);
    }
  }, [location.pathname, lastSearchValue, value]);

  useEffect(() => {
    if (multiSearching) {
      if (isCommaList || isParsedText) {
        setMultisearching(false);
        const commaList = value
          .split(",")
          .map((entry) => entry.trim())
          .filter((entry) => entry !== "");
        const { isMultiSearch, names } = stripMultiSearchBar(value);
        const multiSearchString = `summoners=${(isCommaList ? commaList : names).join(",")}&region=${clientRegion}`;
        const navPath = `/multisearch`;
        if (window) {
          return history.push({
            pathname: navPath,
            search: multiSearchString,
          });
        }
      }
    }
  }, [multiSearching]);

  useEffect(() => {
    if (!loading && searching) {
      setSearching(false);
      let navPath = `/lol/profile/${clientRegion}/${lastSearchValue.toLowerCase().trim()}/overview`;
      if (goToLiveGame) {
        navPath = `/lol/profile/${clientRegion}/${lastSearchValue.toLowerCase().trim()}/live-game`;
      }
      if (error) {
        return history.push({
          pathname: navPath,
          state: { search: 404, searchResults: searchResults },
        });
      } else if (!error) {
        // Add to recently recently searched
        if (window) {
          const recentlySearched = localStorage.getItem("recentlySearched");
          const { profileInit } = data;
          const { playerInfo } = profileInit;
          const { regionId, summonerName, iconId, summonerLevel } = playerInfo;
          const newEntry = {
            key: summonerName.toLowerCase(),
            uniqId: `${regionId}_${summonerName}`.toLowerCase(),
            displayName: summonerName,
            regionId: regionId,
            section: "summoner",
            url: !goToLiveGame
              ? `/lol/profile/${regionId}/${summonerName.toLowerCase()}/overview`
              : `/lol/profile/${regionId}/${summonerName.toLowerCase()}/live-game`,
            iconId,
            summonerLevel,
          };
          if (recentlySearched === null) {
            localStorage.setItem("recentlySearched", JSON.stringify([newEntry]));
          } else {
            const parsed = JSON.parse(recentlySearched);
            const filtered = parsed.filter((item) => item.uniqId !== `${regionId}_${summonerName}`.toLowerCase());
            filtered.unshift(newEntry);
            localStorage.setItem("recentlySearched", JSON.stringify(filtered.slice(0, 6)));
          }
        }

        setLastPage(navPath);
        return history.push({ pathname: navPath });
      }
    }
  }, [loading]);

  const onSearchClick = (e) => {
    search(value);
  };

  const onChange = (event, { newValue, method }) => {
    if (method === "type") {
      !isInteracting && setInteraction(true);
      initInteracting.current = true;
    }

    setValue(newValue);
    if (lastSearchValue) {
      setLastSearchValue(undefined);
    }
  };

  const onMouseDown = (e) => {
    !isInteracting && setInteraction(true);
    initInteracting.current = true;
  };

  const onSuggestionHighlighted = ({ suggestion }) => {
    setHighlightedSuggestion(suggestion);
  };

  const onSuggestionSelected = (event, { suggestion, suggestionValue, method }) => {
    if (suggestionValue && (method === "enter" || method === "click")) {
      if (suggestion.targetHref) {
        return window && window.open(suggestion.url);
      } else if (suggestion.url) {
        history.push({
          pathname: suggestion.url,
          search: championSearchParams || "",
        });
      } else {
        search(suggestionValue);
      }
      inputRef && inputRef.current.blur();
    }
  };

  const onBlur = (event, { highlightedSuggestion }) => {
    initInteracting.current && !isInteracting && setInteraction(true);
  };

  const onSuggestionsFetchRequested = ({ value, reason }) => {
    let suggestions = getSuggestions(value, clientRegion, playerInfoSuggestions);
    if (value.trim().length === 0) {
      suggestions = getRecentlySearched();
    } else {
      // TODO: Remove this line when player infos for all regions are backfilled
      clientRegion === "na1" &&
        debouncedGetPlayerInfoSuggestions({
          variables: {
            query: value.replaceAll(" ", "").toLowerCase(),
            regionId: clientRegion,
          },
        });
    }
    setSuggestions(suggestions);
  };

  const onSuggestionsClearRequested = () => {
    setSuggestions([]);
  };

  let fancySuggest;
  // Placeholder workaround for SSR
  if (!init) {
    return (
      <div className="masthead-ugg-search-bar">
        <div className="react-autosuggest__container react-autosuggest__container_placeholder" />
        <div className="autosuggest-button">{lightModeOn ? <SearchIconWhiteLightMode /> : <SearchIconWhite />}</div>
      </div>
    );
  }

  const inputProps = {
    ref: inputRef,
    placeholder: !isCommaList ? placeholder || "Search Your Summoner" : "Name1, Name2, ...",
    name: "query",
    value,
    onChange,
    spellCheck: false,
    id: "super-search-bar",
    onBlur,
    onMouseDown,
    autoFocus: autoFocus ? autoFocus : false,
  };

  //isLanding only really changes the palceholder animation not what renders
  if (isLanding && value.length === 0) {
    if (animate) {
      fancySuggest = (
        <AnimateOnChange baseClassName="fancy-suggest clear" animationClassName="fancy-suggest-fade-in" animate={animate}>
          {placeholder}
        </AnimateOnChange>
      );
    } else {
      fancySuggest = <span className="fancy-suggest">{placeholder}</span>;
    }
  }

  let finalSuggestions = noDropdown || isCommaList || multisearchOnly ? [] : suggestions;

  const autosuggest = (
    <Autosuggest
      multiSection={true}
      suggestions={finalSuggestions}
      onSuggestionsFetchRequested={onSuggestionsFetchRequested}
      onSuggestionsClearRequested={onSuggestionsClearRequested}
      getSuggestionValue={getSuggestionValue}
      renderSuggestion={renderSuggestion}
      renderSectionTitle={renderSectionTitle}
      getSectionSuggestions={getSectionSuggestions}
      onSuggestionHighlighted={onSuggestionHighlighted}
      onSuggestionSelected={onSuggestionSelected}
      shouldRenderSuggestions={() => isInteracting}
      focusInputOnSuggestionClick={false}
      inputProps={inputProps}
    />
  );

  if (isMasthead) {
    return (
      <div className="masthead-ugg-search-bar">
        {/* <MediaQuery min="MOBILE_SMALL" max="DESKTOP_LARGE">
          {
            (className) => <div className={className}>
              <RegionSelector isMasthead={true} />
            </div>
          }
        </MediaQuery> */}
        {autosuggest}
        <RegionSelectorV2 />
        <div className="autosuggest-button" onClick={onSearchClick}>
          {lightModeOn ? <SearchIconWhiteLightMode /> : <SearchIconWhite />}
        </div>
        {(loading || searching) && (
          <div className="spinthatshit-loader">
            <div className="spinner" />
          </div>
        )}
      </div>
    );
  } else {
    return (
      <div className={`autosuggest-container ${materialDesign ? "autosuggest-material" : ""}`}>
        {isLandingMobile ? (
          <div className="landing-region-selector">
            <RegionSelector forceMastheadRegion={forceMastheadRegion} />
          </div>
        ) : null}
        {autosuggest}
        <div className="autosuggest-button" onClick={onSearchClick}>
          {lightModeOn ? <SearchIconWhiteLightMode /> : <SearchIconWhite />}
        </div>
      </div>
    );
  }
};

export default SearchBar;
